import { type ParseKeys, type TFunction } from 'i18next';
import { hasTranslationKey } from '@pkgs/components/utils/translationKeyUtils';
import { formatNumber } from '@pkgs/components/utils/numberFormatters';
import { getEnergyCertificateTranslationKey as getEnergyCertificateTranslationKeyFromComponents } from '@pkgs/components/utils/energy';
import {
  CountryCode,
  Language,
  type Listing,
  MarketCountryCode,
  type MeasurementSystem,
  PrivacyLevel,
  PropertyMarketingType,
  PropertyType,
} from '@pkgs/api';

import { formatValueWithUnit } from '@/utils/formatPropertyDetails';
import { PageVariant } from '@/types/expose';
import propertyExposeTranslations from '@/i18n/locales/en/property_expose.json' with { type: 'json' };
import { type TRANSLATION_NAMESPACE } from '@/i18n/config';
import { FINANCING_ENABLED_COUNTRIES } from '@/consts/financing';
import { type PropertyFactProps } from '@/components/property-expose/PropertyDetails/PropertyFact';

import { formatIntlList } from './stringUtils';
import { mapLanguageTranslation } from './settings';

const PLACEID_SEPARATOR = '.';
type PropertyExposeTranslationKey = ParseKeys<typeof TRANSLATION_NAMESPACE.property_expose>;
type PropertyTypeTranslationKey = Extract<PropertyExposeTranslationKey, `search.searchbar.advFilter.propertyType.${string}`>;
type PropertySubTypeTranslationKey = Extract<PropertyExposeTranslationKey, `search.searchbar.advFilter.propertySubType.${string}`>;
type PropertyConditionTranslationKey = Extract<PropertyExposeTranslationKey, `search.expose.propertyDetails.condition.${string}`>;
type PropertyFlooringTranslationKey = Extract<PropertyExposeTranslationKey, `search.expose.propertyDetails.label.flooring.${string}`>;
type PropertyBuildingRestrictionsTranslationKey = Extract<
  PropertyExposeTranslationKey,
  `search.expose.propertyDetails.label.buildingRestrictions.${string}`
>;

export const normalizeTranslationKeyPart = (value: string): string => {
  // Reason: backend values may include whitespace which breaks i18n key lookups.
  return value.trim().replaceAll(/\s+/g, '_').toLowerCase();
};

const getPropertyTypeTranslationKey = (propertyType?: string): PropertyTypeTranslationKey | undefined => {
  if (!propertyType) return;

  const translationKey = `search.searchbar.advFilter.propertyType.${normalizeTranslationKeyPart(propertyType)}`;

  return hasTranslationKey<typeof propertyExposeTranslations, PropertyTypeTranslationKey>(propertyExposeTranslations, translationKey)
    ? translationKey
    : undefined;
};

const getPropertySubTypeTranslationKey = (propertySubType?: string): PropertySubTypeTranslationKey | undefined => {
  if (!propertySubType) return;

  const translationKey = `search.searchbar.advFilter.propertySubType.${propertySubType}`;

  return hasTranslationKey<typeof propertyExposeTranslations, PropertySubTypeTranslationKey>(propertyExposeTranslations, translationKey)
    ? translationKey
    : undefined;
};

const getPropertyConditionTranslationKey = (condition?: string): PropertyConditionTranslationKey | undefined => {
  if (!condition) return;

  const translationKey = `search.expose.propertyDetails.condition.${condition}`;

  return hasTranslationKey<typeof propertyExposeTranslations, PropertyConditionTranslationKey>(propertyExposeTranslations, translationKey)
    ? translationKey
    : undefined;
};

const getPropertyFlooringTranslationKey = (flooring?: string): PropertyFlooringTranslationKey | undefined => {
  if (!flooring) return;

  const translationKey = `search.expose.propertyDetails.label.flooring.${normalizeTranslationKeyPart(flooring)}`;

  return hasTranslationKey<typeof propertyExposeTranslations, PropertyFlooringTranslationKey>(propertyExposeTranslations, translationKey)
    ? translationKey
    : undefined;
};

const getPropertyBuildingRestrictionsTranslationKey = (
  buildingRestriction?: string,
): PropertyBuildingRestrictionsTranslationKey | undefined => {
  if (!buildingRestriction) return;

  const translationKey = `search.expose.propertyDetails.label.buildingRestrictions.${normalizeTranslationKeyPart(buildingRestriction)}`;

  return hasTranslationKey<typeof propertyExposeTranslations, PropertyBuildingRestrictionsTranslationKey>(
    propertyExposeTranslations,
    translationKey,
  )
    ? translationKey
    : undefined;
};

export const getLocalityPlaceId = (placeIds: string[]) => {
  const localityPlaceId = placeIds.find((placeId) => placeId.includes('locality.'));
  if (!localityPlaceId) {
    // fallback to the highest administrative area in case there is no locality
    // Google Maps admin area levels go from 1 to 7, so a simple sort should do the trick to get the highest level
    const orderedAdminAreas = placeIds
      .filter((placeIds) => placeIds.includes('administrative_area_level_'))
      .sort()
      .reverse();
    const highestAdminArea = orderedAdminAreas[0];
    return highestAdminArea ? highestAdminArea.split(PLACEID_SEPARATOR)[1] : '';
  }
  return localityPlaceId.split(PLACEID_SEPARATOR)[1];
};

export const getOfficeNameForLabels = (officeName?: string, shopName?: string) => {
  return officeName ?? (shopName ? getParsedShopName(shopName) : 'Engel & Völkers');
};

export const getCourtagePassusTitleTranslationKey = (property: Listing) =>
  property.countryAlpha2 === CountryCode.DE && property.propertyMarketingType === PropertyMarketingType.rent
    ? 'search.expose.propertyDetails.label.courtagePassusRentalGermanyProperty'
    : 'search.expose.propertyDetails.label.courtagePassus';

export const getParsedShopName = (shopName: string): string => {
  const regexName = /Engel & Völkers/g;
  const replacedShopName = shopName.replaceAll(regexName, '');

  return replacedShopName;
};

export const getPlaceIdForLocationContext = (placeIds: string[]) => {
  const neighborhoodPlaceId = placeIds.find((placeId) => placeId.includes('neighborhood'));
  if (neighborhoodPlaceId) {
    return neighborhoodPlaceId.split(PLACEID_SEPARATOR)[1];
  }
  // Google Maps sublocality levels go from 1 to 5, so a simple sort should do the trick to get the highest level
  const orderedSublocalities = placeIds
    .filter((placeIds) => placeIds.includes('sublocality_level'))
    .sort()
    .reverse();
  const highestSublocality = orderedSublocalities[0];
  if (highestSublocality) {
    return highestSublocality.split(PLACEID_SEPARATOR)[1] ?? getLocalityPlaceId(placeIds);
  }
  return getLocalityPlaceId(placeIds);
};

export const getEnergyCertificateTranslationKey = getEnergyCertificateTranslationKeyFromComponents;

export const getPropertyAvailableLanguageMessage = (
  t: TFunction<typeof TRANSLATION_NAMESPACE.property_expose>,
  language: Language,
  availableLanguages: Array<string>,
): string => {
  const availableLanguagesLabel: string[] = availableLanguages
    .filter((lang) => lang in Language)
    .map((lang) => t(mapLanguageTranslation[lang.toLowerCase() as Language] as ParseKeys<typeof TRANSLATION_NAMESPACE.property_expose>));
  const languages = formatIntlList(availableLanguagesLabel, language);

  return t('search.expose.label.availableLanguagesBanner', {
    languages,
  });
};

export const showsSimilarPropertiesButton = (property: Listing): boolean =>
  Boolean(property.autogenPageUrl) &&
  new Set<PropertyType>([PropertyType.house, PropertyType.apartment, PropertyType.land]).has(property.propertyType);

export const isIndexedProperty = (property: Listing, language: Language, pageLanguages: string[] | null, countryCode: MarketCountryCode) =>
  Boolean(
    property.countryAlpha2 &&
    countryCode === property.countryAlpha2 &&
    property.languages?.includes(language) &&
    language !== Language.ca &&
    pageLanguages?.includes(language),
  ); // https://engelvoelkers.atlassian.net/browse/NS-5401

export const isIndexedExpose = ({
  listing,
  pageVariant,
  countryCode,
  language,
  pageLanguages,
}: {
  listing: Listing | undefined;
  pageVariant: PageVariant;
  countryCode: MarketCountryCode;
  language: Language;
  pageLanguages: string[] | null;
}): boolean => {
  if (
    pageVariant !== PageVariant.PRIVATE_OFFICE_PUBLIC &&
    pageVariant !== PageVariant.EXPOSE &&
    pageVariant !== PageVariant.DEVELOPMENT_PROJECT
  ) {
    return false;
  }

  if (countryCode === MarketCountryCode['private-office'] || !listing) {
    return false;
  }

  if (listing.privacyLevel !== PrivacyLevel.PUBLIC && listing.privacyLevel !== PrivacyLevel.RESTRICTED) {
    return false;
  }

  return isIndexedProperty(listing, language, pageLanguages, countryCode);
};

export const hasFinancingSection = (property: Listing) =>
  !!property.countryAlpha2 &&
  !property.isPrivateOffice &&
  FINANCING_ENABLED_COUNTRIES.has(property.countryAlpha2) &&
  property.propertyMarketingType === PropertyMarketingType.sale;

export const hasExtraInfo = (propertyData: Listing): boolean => {
  return Boolean(
    propertyData.rooms?.min ||
    propertyData.bedrooms?.min ||
    propertyData.bathrooms?.min ||
    propertyData.area?.livingSurface?.min ||
    propertyData.area?.totalSurface?.min ||
    propertyData.area?.officeSurface?.min ||
    propertyData.area?.salesSurface?.min ||
    propertyData.area?.storageSurface?.min ||
    propertyData.area?.terraceSurface?.min ||
    propertyData.area?.dividableFrom?.min ||
    propertyData.area?.plotSurface?.min ||
    propertyData.parkingBays?.min ||
    propertyData.condition ||
    propertyData.propertySubType ||
    propertyData.constructionYear?.min ||
    propertyData.propertyType ||
    propertyData.garages?.min ||
    propertyData.flooring ||
    propertyData.buildingRestrictions?.length ||
    propertyData.floors?.min,
  );
};

export const hasEnergyInfo = (propertyData: Listing): boolean => {
  return Boolean(
    getEnergyCertificateTranslationKey(propertyData.propertyType, propertyData.countryAlpha2, propertyData.energyCertAvailable) ||
    (propertyData.epCode && propertyData.countryAlpha2 === CountryCode.BE) ||
    (propertyData.co2Emission && propertyData.countryAlpha2 === CountryCode.BE) ||
    propertyData.energyCertType ||
    propertyData.energyClassNormalized ||
    propertyData.energyEfficiencyFactor ||
    propertyData.finalEnergyConsumption ||
    propertyData.energyFinalDemand ||
    propertyData.heatingType ||
    propertyData.heatingMaterial ||
    propertyData.heatingConsumption ||
    propertyData.finalHeatingConsumption ||
    propertyData.co2EmissionRate ||
    propertyData.finalPowerConsumption,
  );
};

export const hasCountrySpecificPropertyDetail = (propertyData: Listing): boolean => {
  // replace be with countryAlpha2 in the future when we have other countries with specific properties
  return Boolean(propertyData.countrySpecific?.['be']);
};

export const getPropertyDetails = (
  property: Listing,
  livingAreaLabel: ParseKeys<'property_expose'>,
  showsConstructionYear: boolean = false,
  t: TFunction<typeof TRANSLATION_NAMESPACE.property_expose>,
  language: Language,
  measurementSystem: MeasurementSystem,
): PropertyFactProps[] => {
  const formatOptions = { maximumFractionDigits: 2 };
  const propertyDetails: PropertyFactProps[] = [
    {
      label: t(property.propertySubType ? 'search.expose.propertyDetails.label.propertySubtype' : 'search.searchbar.propertyType'),
      content: property.propertySubType
        ? getPropertySubTypeTranslationKey(property.propertySubType)
          ? t(getPropertySubTypeTranslationKey(property.propertySubType)!)
          : ''
        : property.propertyType
          ? getPropertyTypeTranslationKey(property.propertyType)
            ? t(getPropertyTypeTranslationKey(property.propertyType)!)
            : ''
          : '',
    },
    {
      label: t('search.expose.propertyDetails.label.condition'),
      content: property.condition
        ? getPropertyConditionTranslationKey(property.condition)
          ? t(getPropertyConditionTranslationKey(property.condition)!)
          : ''
        : '',
    },
    {
      label: t('search.expose.propertyDetails.label.rooms', {
        count: 2, // hardcode labels to plural
      }),
      content: property.rooms?.min ? formatNumber({ number: property.rooms.min, language }, formatOptions) : '',
    },
    {
      label: t('search.expose.propertyDetails.label.bedrooms', {
        count: 2, // hardcode labels to plural
      }),
      content: property.bedrooms?.min ? formatNumber({ number: property.bedrooms.min, language }, formatOptions) : '',
    },
    {
      label: t('search.expose.propertyDetails.label.bathrooms', {
        count: 2, // hardcode labels to plural
      }),
      content: property.bathrooms?.min ? formatNumber({ number: property.bathrooms.min, language }, formatOptions) : '',
    },
    {
      label: t('search.expose.propertyDetails.label.floor'),
      content: property.floor?.min ? formatNumber({ number: property.floor.min, language }, formatOptions) : '',
    },
    {
      label: t('search.expose.propertyDetails.label.numberOfFloors'),
      content: property.floors?.min ? formatNumber({ number: property.floors.min, language }, formatOptions) : '',
    },
    {
      label: t('search.expose.propertyDetails.label.garage', {
        count: 2, // hardcode labels to plural
      }),
      content: property.garages?.min ? formatNumber({ number: property.garages.min, language }, formatOptions) : '',
    },
    {
      label: t('search.expose.propertyDetails.label.parkingBays'),
      content: property.parkingBays?.min ? formatNumber({ number: property.parkingBays.min, language }, formatOptions) : '',
    },
    {
      label: t('search.expose.propertyDetails.label.totalSurface'),
      content: property.area?.totalSurface?.min
        ? formatValueWithUnit({
            value: property.area.totalSurface.min,
            language,
            measurementSystem,
            isSurface: true,
            shouldShowRoundedValue: true,
          })
        : '',
    },
    {
      label: t(livingAreaLabel),
      content: property.area?.livingSurface?.min
        ? formatValueWithUnit({
            value: property.area.livingSurface.min,
            language,
            measurementSystem,
            isSurface: true,
            shouldShowRoundedValue: true,
          })
        : '',
    },
    {
      label: t('search.expose.propertyDetails.label.plotSurface'),
      content: property.area?.plotSurface?.min
        ? formatValueWithUnit({
            value: property.area.plotSurface.min,
            language,
            measurementSystem,
            isSurface: true,
            shouldShowRoundedValue: property.countryAlpha2 !== CountryCode.CH,
          })
        : '',
    },
    {
      label: t('search.expose.propertyDetails.label.shopSurface'),
      content: property.area?.shopSurface?.min
        ? formatValueWithUnit({
            value: property.area.shopSurface.min,
            language,
            measurementSystem,
            isSurface: true,
            shouldShowRoundedValue: true,
          })
        : '',
    },
    {
      label: t('search.expose.propertyDetails.label.terraceSurface'),
      content: property.area?.terraceSurface?.min
        ? formatValueWithUnit({
            value: property.area.terraceSurface.min,
            language,
            measurementSystem,
            isSurface: true,
            shouldShowRoundedValue: true,
          })
        : '',
    },
    {
      label: t('search.expose.propertyDetails.label.officeSurface'),
      content: property.area?.officeSurface?.min
        ? formatValueWithUnit({
            value: property.area.officeSurface.min,
            language,
            measurementSystem,
            isSurface: true,
            shouldShowRoundedValue: true,
          })
        : '',
    },
    {
      label: t('search.expose.propertyDetails.label.storageSurface'),
      content: property.area?.storageSurface?.min
        ? formatValueWithUnit({
            value: property.area.storageSurface.min,
            language,
            measurementSystem,
            isSurface: true,
            shouldShowRoundedValue: true,
          })
        : '',
    },
    {
      label: t('search.expose.propertyDetails.label.salesSurface'),
      content: property.area?.salesSurface?.min
        ? formatValueWithUnit({
            value: property.area.salesSurface.min,
            language,
            measurementSystem,
            isSurface: true,
            shouldShowRoundedValue: true,
          })
        : '',
    },
    {
      label: t('search.expose.propertyDetails.label.dividableFrom'),
      content: property.area?.dividableFrom?.min
        ? formatValueWithUnit({
            value: property.area.dividableFrom.min,
            language,
            measurementSystem,
            isSurface: true,
            shouldShowRoundedValue: true,
          })
        : '',
    },
    {
      label: t('search.expose.propertyDetails.label.usableSurface'),
      content: property.area?.usableSurface?.min
        ? formatValueWithUnit({
            value: property.area.usableSurface.min,
            language,
            measurementSystem,
            isSurface: true,
            shouldShowRoundedValue: true,
          })
        : '',
    },
    {
      label: t('search.expose.propertyDetails.label.commercialSurface'),
      content: property.area?.commercialSurface?.min
        ? formatValueWithUnit({
            value: property.area.commercialSurface.min,
            language,
            measurementSystem,
            isSurface: true,
            shouldShowRoundedValue: true,
          })
        : '',
    },
    {
      label: t('search.expose.propertyDetails.label.gardenSurface'),
      content: property.area?.gardenSurface?.min
        ? formatValueWithUnit({
            value: property.area.gardenSurface.min,
            language,
            measurementSystem,
            isSurface: true,
            shouldShowRoundedValue: true,
          })
        : '',
    },
    {
      label: t('search.expose.propertyDetails.label.roiCurrent'),
      content: property.roiCurrent?.min
        ? `${formatNumber({ number: property.roiCurrent.min, language }, { maximumFractionDigits: 2 })} %`
        : '',
    },
    {
      label: t('search.expose.propertyDetails.label.roiTarget'),
      content: property.roiTarget?.min
        ? `${formatNumber({ number: property.roiTarget.min, language }, { maximumFractionDigits: 2 })} %`
        : '',
    },
    {
      label: t('search.expose.propertyDetails.label.flooring'),
      content: property.flooring
        ?.map((floor) => getPropertyFlooringTranslationKey(String(floor)))
        .filter((flooringKey): flooringKey is PropertyFlooringTranslationKey => Boolean(flooringKey))
        .map((flooringKey) => t(flooringKey))
        .join(', '),
    },
    {
      label: t('search.expose.propertyDetails.label.buildingRestrictions'),
      content: property.buildingRestrictions
        ?.map((buildingRestriction) => getPropertyBuildingRestrictionsTranslationKey(String(buildingRestriction)))
        .filter((buildingRestrictionKey): buildingRestrictionKey is PropertyBuildingRestrictionsTranslationKey =>
          Boolean(buildingRestrictionKey),
        )
        .map((buildingRestrictionKey) => t(buildingRestrictionKey))
        .join(', '),
    },
    {
      label: t('search.expose.propertyDetails.label.balconyAmount'),
      content: property.balconyAmount?.min ? formatNumber({ number: property.balconyAmount.min, language }, formatOptions) : '',
    },
  ];

  if (!hasEnergyInfo(property)) {
    propertyDetails.push({
      label: t('search.expose.propertyDetails.label.constructionYear'),
      content: showsConstructionYear ? property.constructionYear?.min : null,
    });
  }

  return propertyDetails.filter((detail) => detail.content);
};
